import type { SceneNode } from "../scene/model";
import { createTextNode } from "./text";

export const blockTemplates = [
  {
    id: "classic",
    name: "Классический",
    color: "#92633e",
    radius: 22,
    labelRadius: 14,
    light: 96,
    labelLight: 87,
    saturation: 36,
    dashed: false,
    outlineBadge: false,
    right: false,
    family: "Georgia",
  },
  {
    id: "soft",
    name: "Мягкая плашка",
    color: "#548665",
    radius: 30,
    labelRadius: 24,
    light: 95,
    labelLight: 35,
    saturation: 30,
    dashed: false,
    outlineBadge: false,
    right: false,
    family: "Arial",
  },
  {
    id: "outline",
    name: "Лёгкий контур",
    color: "#a65572",
    radius: 18,
    labelRadius: 6,
    light: 99,
    labelLight: 92,
    saturation: 42,
    dashed: false,
    outlineBadge: true,
    right: false,
    family: "Georgia",
  },
  {
    id: "notebook",
    name: "Пунктирная рамка",
    color: "#327ea7",
    radius: 12,
    labelRadius: 8,
    light: 97,
    labelLight: 88,
    saturation: 48,
    dashed: true,
    outlineBadge: false,
    right: false,
    family: "Arial",
  },
  {
    id: "ribbon",
    name: "Лента",
    color: "#8663ad",
    radius: 24,
    labelRadius: 4,
    light: 96,
    labelLight: 39,
    saturation: 38,
    dashed: false,
    outlineBadge: false,
    right: true,
    family: "Arial",
  },
] as const;
export type BlockTemplateId = (typeof blockTemplates)[number]["id"];
export type BlockRole = "body" | "label" | "badge" | "number" | "title";
const roles: BlockRole[] = ["body", "label", "badge", "number", "title"];
export interface BlockConfig {
  version: 1;
  template: BlockTemplateId;
  color: string;
  fontFamily: string;
  fontSize: number;
  number: string;
  title: string;
}
export function blockConfig(node: SceneNode): BlockConfig | undefined {
  return node.layer.block as BlockConfig | undefined;
}
export function isBlockPart(node: SceneNode) {
  return typeof node.layer.blockRole === "string";
}
export function blockRoots(nodes: readonly SceneNode[]) {
  return nodes.filter((n) => n.layer.blockRole === "body" && blockConfig(n));
}
/** A block participates in selection/alignment as one entity. */
export function selectionEntities(nodes: readonly SceneNode[]) {
  const groups = new Set(blockRoots(nodes).map((n) => n.layer.groupId));
  return nodes.filter(
    (n) =>
      !isBlockPart(n) ||
      !groups.has(n.layer.groupId) ||
      n.layer.blockRole === "body",
  );
}
export function blockMinimum(config: BlockConfig) {
  return {
    width: Math.max(180, config.fontSize * 7.5),
    height: Math.max(100, config.fontSize * 3.5),
  };
}
function hue(color: string) {
  const [r, g, b] = [1, 3, 5].map(
    (i) => parseInt(color.slice(i, i + 2), 16) / 255,
  ) as [number, number, number];
  const hi = Math.max(r, g, b),
    lo = Math.min(r, g, b),
    d = hi - lo;
  if (d < 0.01) return null;
  return (
    ((hi === r ? (g - b) / d : hi === g ? (b - r) / d + 2 : (r - g) / d + 4) *
      60 +
      360) %
    360
  );
}
function tone(h: number | null, saturation: number, lightness: number) {
  const s = h === null ? 0 : saturation / 100,
    l = lightness / 100;
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + (h ?? 0) / 30) % 12;
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))))
      .toString(16)
      .padStart(2, "0");
  };
  return "#" + channel(0) + channel(8) + channel(4);
}
export function blockPalette(config: BlockConfig) {
  const t = blockTemplates.find((t) => t.id === config.template)!;
  const h = hue(config.color);
  return {
    body: tone(h, t.saturation, t.light),
    stroke: tone(h, t.saturation, 38),
    label: tone(h, t.saturation, t.labelLight),
    badge: tone(h, t.saturation, 38),
    title: t.labelLight < 55 ? "#ffffff" : tone(h, t.saturation, 16),
    number: t.outlineBadge ? tone(h, t.saturation, 25) : "#ffffff",
  };
}
export function assertBlocks(nodes: readonly SceneNode[]) {
  const groups = new Map<unknown, SceneNode[]>();
  for (const n of nodes) {
    if (!isBlockPart(n) && n.layer.block === undefined) continue;
    if (
      typeof n.layer.groupId !== "string" ||
      !n.layer.groupId ||
      !roles.includes(n.layer.blockRole as BlockRole)
    )
      throw new Error("Повреждены связи блока.");
    const members = groups.get(n.layer.groupId) ?? [];
    members.push(n);
    groups.set(n.layer.groupId, members);
  }
  for (const [id, members] of groups) {
    const root = members.find((n) => n.layer.blockRole === "body"),
      c = root && blockConfig(root);
    if (
      members.length !== 5 ||
      nodes.filter((n) => n.layer.groupId === id).length !== 5 ||
      new Set(members.map((n) => n.layer.blockRole)).size !== 5 ||
      !c ||
      c.version !== 1 ||
      !blockTemplates.some((t) => t.id === c.template) ||
      typeof c.color !== "string" ||
      !/^#[a-f0-9]{6}$/i.test(c.color) ||
      typeof c.fontFamily !== "string" ||
      !c.fontFamily.trim() ||
      c.fontFamily.length > 150 ||
      !Number.isFinite(c.fontSize) ||
      c.fontSize < 12 ||
      c.fontSize > 60 ||
      typeof c.number !== "string" ||
      c.number.length > 4 ||
      typeof c.title !== "string" ||
      c.title.length > 200 ||
      members.some(
        (n) =>
          n.kind !==
            (["number", "title"].includes(String(n.layer.blockRole))
              ? "text"
              : n.layer.blockRole === "badge"
                ? "ellipse"
                : "rectangle") ||
          (n !== root && n.layer.block !== undefined),
      )
    )
      throw new Error("Повреждены параметры блока.");
  }
}
/** Layout stays in document units: resizing the body never scales its badge or type. */
export function layoutBlock(
  members: readonly SceneNode[],
  paint = false,
): SceneNode[] {
  const root = blockRoots(members)[0];
  if (!root) return members.slice();
  const c = blockConfig(root)!,
    t = blockTemplates.find((t) => t.id === c.template)!;
  const min = blockMinimum(c),
    w = Math.max(min.width, root.geometry.width!),
    h = Math.max(min.height, root.geometry.height!);
  const size = c.fontSize,
    badge = Math.max(42, size * 1.7),
    pad = 14,
    gap = 10;
  const header = Math.max(badge, size * 1.8),
    labelW = w - pad * 2 - badge - gap;
  const badgeX = t.right ? w - pad - badge / 2 : pad + badge / 2;
  const labelX = t.right ? pad + labelW / 2 : pad + badge + gap + labelW / 2;
  const boxes: Record<BlockRole, number[]> = {
    body: [w / 2, h / 2, w, h],
    label: [labelX, pad + header / 2, labelW, header],
    badge: [badgeX, pad + header / 2, badge, badge],
    number: [badgeX, pad + header / 2, badge, size * 1.25],
    title: [labelX, pad + header / 2, labelW - 16, size * 1.25],
  };
  const angle = ((root.transform.rotation ?? 0) * Math.PI) / 180,
    co = Math.cos(angle),
    si = Math.sin(angle);
  const k = Math.tan(((root.transform.skewX ?? 0) * Math.PI) / 180),
    colors = blockPalette(c);
  return members.map((n) => {
    const role = n.layer.blockRole as BlockRole,
      [x, y, width, height] = boxes[role]!;
    const dx = x! - w / 2,
      dy = y! - h / 2;
    const next: SceneNode = {
      ...n,
      geometry: {
        ...n.geometry,
        width: width!,
        height: height!,
        ...(["body", "label"].includes(role)
          ? { cornerRadius: role === "body" ? t.radius : t.labelRadius }
          : {}),
      },
      transform: {
        ...root.transform,
        x: root.transform.x! + co * (dx + k * dy) - si * dy,
        y: root.transform.y! + si * (dx + k * dy) + co * dy,
      },
    };
    if (n.kind === "text")
      next.text = {
        ...n.text,
        value: role === "number" ? c.number : c.title,
        family: c.fontFamily,
        size,
        layoutWidth: width!,
        layoutHeight: height!,
        frame: true,
        align: "center",
        styles: {},
        bold: true,
        lineSpacing: 1,
        letterSpacing: 0,
        ...(paint
          ? { color: role === "number" ? colors.number : colors.title }
          : {}),
      };
    if (paint)
      next.appearance = {
        ...n.appearance,
        fill:
          role === "body"
            ? colors.body
            : role === "label"
              ? colors.label
              : role === "badge"
                ? t.outlineBadge
                  ? colors.body
                  : colors.badge
                : colors[role],
        ...(n.kind === "text"
          ? {}
          : {
              stroke: colors.stroke,
              strokeWidth:
                role === "body"
                  ? 2
                  : role === "badge" && t.outlineBadge
                    ? 2
                    : 0,
              strokeEnabled:
                role === "body" || (role === "badge" && t.outlineBadge),
              lineStyle: role === "body" && t.dashed ? "dashed" : "solid",
            }),
      };
    return next;
  });
}
export function createBlock(
  template: BlockTemplateId,
  x: number,
  y: number,
  newId: () => string,
): SceneNode[] {
  const t = blockTemplates.find((t) => t.id === template)!;
  const groupId = newId();
  const config: BlockConfig = {
    version: 1,
    template,
    color: t.color,
    fontFamily: t.family,
    fontSize: 24,
    number: "1",
    title: "Название задания",
  };
  const nodes = roles.map((role) => {
    const n: SceneNode = ["number", "title"].includes(role)
      ? createTextNode(newId(), x, y, true)
      : {
          id: newId(),
          kind: role === "badge" ? "ellipse" : "rectangle",
          transform: { x, y, originX: "center", originY: "center" },
          geometry: { width: 480, height: 280 },
          appearance: { opacity: 1 },
          text: {},
          interaction: { role: "decorShape" },
          effects: {},
          layer: {},
          media: {},
        };
    n.layer = {
      name: {
        body: "Рамка",
        label: "Плашка",
        badge: "Кружок",
        number: "Номер",
        title: "Заголовок",
      }[role],
      groupId,
      groupName: "Блок · " + t.name,
      groupCollapsed: true,
      blockRole: role,
      ...(role === "body" ? { block: config } : {}),
    };
    return n;
  });
  return layoutBlock(nodes, true);
}
export function updateBlock(
  nodes: readonly SceneNode[],
  group: string,
  patch: Partial<BlockConfig>,
) {
  const members = nodes
    .filter((n) => n.layer.groupId === group)
    .map((n) =>
      n.layer.blockRole === "body"
        ? {
            ...n,
            layer: { ...n.layer, block: { ...blockConfig(n)!, ...patch } },
          }
        : n,
    );
  assertBlocks(members);
  const changes = new Map(layoutBlock(members, true).map((n) => [n.id, n]));
  return nodes.map((n) => changes.get(n.id) ?? n);
}
/** Reflow at the scene boundary covers pointer, dimensions, nudge, paste and history commands. */
export function reflowBlocks(
  nodes: SceneNode[],
  previous: readonly SceneNode[],
) {
  // Destructive crop/merge can consume only some primitives. Keep their result
  // as ordinary elements instead of retaining a broken smart-block reference.
  const groups = new Set(nodes.filter(isBlockPart).map((n) => n.layer.groupId));
  for (const group of groups) {
    try {
      assertBlocks(nodes.filter((n) => n.layer.groupId === group));
    } catch {
      nodes = nodes.map((n) => {
        if (n.layer.groupId !== group) return n;
        const layer = { ...n.layer };
        delete layer.block;
        delete layer.blockRole;
        return { ...n, layer };
      });
    }
  }
  const roots = blockRoots(nodes);
  if (!roots.length) return nodes;
  const old = new Map(previous.map((n) => [n.id, n])),
    changes = new Map<string, SceneNode>();
  for (const root of roots) {
    const before = old.get(root.id);
    if (
      before &&
      root.geometry === before.geometry &&
      root.transform === before.transform &&
      root.layer.block === before.layer.block
    )
      continue;
    const members = nodes.filter((n) => n.layer.groupId === root.layer.groupId);
    for (const n of layoutBlock(members)) changes.set(n.id, n);
  }
  return changes.size ? nodes.map((n) => changes.get(n.id) ?? n) : nodes;
}
